/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { ChipSelectMulti, ChipSelectSingle } from '@/shared/ui/components/form/ChipSelect';
import { TagInput } from '@/shared/ui/components/form/TagInput';
import { translatePropertyChipLabel } from '@/shared/utils/property-chip-label';
import {
  AMENITIES_INTERNAL_OPTIONS,
  AMENITIES_EXTERNAL_OPTIONS,
  AMENITIES_SECURITY_OPTIONS,
  CONDO_LEISURE_OPTIONS,
  CONDO_SECURITY_OPTIONS,
} from '@features/property-management/components/filter-panel/constants';
import { FURNISHED_OPTIONS } from '@features/property-management/components/PropertyFormDualPane/schema.constants';
import type { ClientFormData } from './schema';
import { labelStyle } from './drawer-styles';
import { Label, TextInput } from '@/shared/ui';

type DemandPath =
  | 'demandProfile.amenitiesInternal'
  | 'demandProfile.amenitiesExternal'
  | 'demandProfile.amenitiesSecurity'
  | 'demandProfile.condoLeisure'
  | 'demandProfile.condoSecurity';

interface Props {
  form: UseFormReturn<ClientFormData>;
}

/** The minimums that feed the match score, in the order a broker asks them. */
const MINIMUM_FIELDS = [
  { name: 'demandProfile.bedroomsMin', label: 'drawer.preferences.bedroomsMin', maxLength: 2 },
  { name: 'demandProfile.bathroomsMin', label: 'drawer.preferences.bathroomsMin', maxLength: 2 },
  { name: 'demandProfile.suitesMin', label: 'drawer.preferences.suitesMin', maxLength: 2 },
  { name: 'demandProfile.parkingMin', label: 'drawer.preferences.parkingMin', maxLength: 2 },
  { name: 'demandProfile.areaMin', label: 'drawer.preferences.areaMin', maxLength: 7 },
] as const;

/** Amenity chip groups, mirrored from the property filter panel. */
const AMENITY_GROUPS = [
  { name: 'demandProfile.amenitiesInternal', label: 'drawer.preferences.amenitiesInternal', options: AMENITIES_INTERNAL_OPTIONS },
  { name: 'demandProfile.amenitiesExternal', label: 'drawer.preferences.amenitiesExternal', options: AMENITIES_EXTERNAL_OPTIONS },
  { name: 'demandProfile.amenitiesSecurity', label: 'drawer.preferences.amenitiesSecurity', options: AMENITIES_SECURITY_OPTIONS },
  { name: 'demandProfile.condoLeisure', label: 'drawer.preferences.condoLeisure', options: CONDO_LEISURE_OPTIONS },
  { name: 'demandProfile.condoSecurity', label: 'drawer.preferences.condoSecurity', options: CONDO_SECURITY_OPTIONS },
] as const satisfies ReadonlyArray<{ name: DemandPath; label: string; options: readonly string[] }>;

/** Minimum room counts and area, as digit-only inputs. */
export const MinimumFields: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation('features/clients');

  return (
    <div className="grid grid-cols-2 gap-(--space-3) sm:grid-cols-3">
      {MINIMUM_FIELDS.map(({ name, label, maxLength }) => (
        <Controller
          key={name}
          name={name}
          control={form.control}
          render={({ field }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t(label)}</Label>
              <TextInput
                {...field}
                value={field.value || ''}
                inputMode="numeric"
                placeholder="0"
                onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, maxLength))}
              />
            </div>
          )}
        />
      ))}
    </div>
  );
};

/** Furnishing level and the pet question — both clearable by re-clicking. */
export const FurnishedAndPetFields: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation('features/clients');
  const { t: tProp } = useTranslation('features/properties');

  const furnishedOptions = FURNISHED_OPTIONS.map((value) => ({
    value,
    label: translatePropertyChipLabel(tProp, value),
  }));
  const petOptions = (['yes', 'no'] as const).map((value) => ({
    value,
    label: t(`drawer.preferences.petOptions.${value}`),
  }));

  return (
    <>
      <Controller
        name="demandProfile.furnished"
        control={form.control}
        render={({ field }) => (
          <ChipSelectSingle
            label={t('drawer.preferences.furnished')}
            options={furnishedOptions}
            value={field.value || ''}
            onChange={(value) => field.onChange(field.value === value ? '' : value)}
          />
        )}
      />
      <Controller
        name="demandProfile.petFriendly"
        control={form.control}
        render={({ field }) => (
          <ChipSelectSingle
            label={t('drawer.preferences.petFriendly')}
            options={petOptions}
            value={field.value || ''}
            onChange={(value) => field.onChange(field.value === value ? '' : value)}
          />
        )}
      />
    </>
  );
};

/** The five amenity chip groups. */
export const AmenityGroupFields: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation('features/clients');
  const { t: tProp } = useTranslation('features/properties');

  return (
    <>
      {AMENITY_GROUPS.map(({ name, label, options }) => (
        <Controller
          key={name}
          name={name}
          control={form.control}
          render={({ field }) => (
            <ChipSelectMulti
              label={t(label)}
              options={options.map((value) => ({ value, label: translatePropertyChipLabel(tProp, value) }))}
              value={field.value || []}
              onChange={field.onChange}
              optionsClassName="max-h-[8rem] overflow-y-auto pr-(--space-1)"
            />
          )}
        />
      ))}
    </>
  );
};

/** Free-form wishes no predefined chip covers. */
export const PreferenceTagsField: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation('features/clients');

  return (
    <Controller
      name="demandProfile.tags"
      control={form.control}
      render={({ field }) => (
        <TagInput
          label={t('drawer.preferences.tags')}
          value={field.value || []}
          onChange={field.onChange}
          placeholder={t('drawer.preferences.tagsPlaceholder')}
        />
      )}
    />
  );
};
